import { createClient, type SupabaseClient } from '@supabase/supabase-js'

/**
 * Supabase client.
 *
 * The anon key is meant to be public and ships inside the bundle: Row Level
 * Security is what actually protects the data, not the secrecy of this key.
 * The service_role key must never appear here.
 *
 * Both values are optional at build time so the app still runs, entirely
 * offline and local-only, when no backend is configured — no login, no sync,
 * no push, and a working UI. The test suite pins that mode.
 */
const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

export const isSyncConfigured = Boolean(url && anonKey)

export const supabase: SupabaseClient | null = isSyncConfigured
  ? createClient(url!, anonKey!, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        // The app has no URL-based auth callbacks; email OTP is verified
        // in-page, so there is nothing to detect in the address bar.
        detectSessionInUrl: false,
      },
    })
  : null

/** Narrowing helper for the many call sites that need a configured client. */
export function requireSupabase(): SupabaseClient {
  if (!supabase) throw new Error('Supabase is not configured')
  return supabase
}

/**
 * The signed-in user's id, or null.
 *
 * `getSession` reads the stored session and refreshes it only when it has
 * actually expired. `getUser`, which the draft effectively did through
 * `ensureAnonymousUser`, always went to `/auth/v1/user` — a network round trip
 * in front of every call, and the source of the "JWT expired" failure a phone
 * reports when it wakes up with a stale access token.
 *
 * It never creates a session. Nothing in this app may sign a user in as a side
 * effect: that is what `signInAnonymously()` did, and an anonymous session
 * bound to one browser profile is worse than no sync at all.
 */
export async function currentUserId(): Promise<string | null> {
  if (!supabase) return null
  const { data } = await supabase.auth.getSession()
  return data.session?.user.id ?? null
}
